import type { CSSProperties } from 'react';
import { useId } from 'react';

/** Radium brand mark: stacked cards + Ra glyph. Matches design `primitives.jsx` (Desktop/design). */
export interface AtomicMarkProps {
  size?: number;
  tone?: string;
  spinning?: boolean;
  /** Force monochrome silhouette (default: auto — mono when size &lt; 28). */
  mono?: boolean;
  style?: CSSProperties;
}

export function AtomicMark({
  size = 16,
  tone = 'currentColor',
  spinning = false,
  mono,
  style,
}: AtomicMarkProps) {
  const rawId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const uid = rawId || 'am';

  if (spinning) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        className="atomic-mark-spin"
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          flexShrink: 0,
          animation: 'atomic-spin 1.6s linear infinite',
          ...style,
        }}
        aria-hidden="true"
      >
        <style>{`@keyframes atomic-spin { to { transform: rotate(360deg); } } @media (prefers-reduced-motion: reduce) { .atomic-mark-spin { animation: none !important; } }`}</style>
        <ellipse
          cx="12"
          cy="12"
          rx="10"
          ry="4"
          stroke={tone}
          strokeWidth="1.25"
          transform="rotate(-30 12 12)"
          opacity="0.85"
        />
        <circle cx="12" cy="12" r="2.6" fill={tone} />
      </svg>
    );
  }

  const useMono = mono ?? size < 28;

  if (useMono) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 716 716"
        fill="none"
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          flexShrink: 0,
          ...style,
        }}
        aria-hidden="true"
      >
        <path
          d="M5.64 60.36C8.76 60.36 11.28 62.89 11.28 66.01V662.06C11.28 685.25 30.09 704.06 53.28 704.06H660.05C663.17 704.06 665.7 706.58 665.7 709.7C665.7 712.81 663.17 715.34 660.05 715.34H42C18.8 715.34 0 696.54 0 673.34V66.01C0 62.89 2.53 60.36 5.64 60.36Z"
          fill={tone}
          opacity="0.55"
        />
        <path
          d="M28.21 37.8C31.32 37.8 33.85 40.32 33.85 43.44V639.49C33.85 662.69 52.65 681.49 75.85 681.49H682.62C685.74 681.49 688.26 684.02 688.26 687.13C688.26 690.25 685.74 692.77 682.62 692.77H64.57C41.37 692.77 22.57 673.97 22.57 650.77V43.44C22.57 40.32 25.09 37.8 28.21 37.8Z"
          fill={tone}
          opacity="0.75"
        />
        <rect
          x="45.13"
          y="0"
          width="670.21"
          height="670.21"
          rx="42"
          fill={tone}
        />
        <path
          d="M224.838 450H292.338L352.338 540H275.838L224.838 450ZM163.338 330H229.338V540H163.338V330ZM205.938 385.5V330H251.838C272.438 330 289.138 333.1 301.938 339.3C314.938 345.5 324.438 354 330.438 364.8C336.638 375.6 339.738 388 339.738 402C339.738 415.8 336.638 428.2 330.438 439.2C324.438 450 314.938 458.5 301.938 464.7C289.138 470.9 272.438 474 251.838 474H205.938V424.5H245.838C251.438 424.5 256.038 423.8 259.638 422.4C263.238 420.8 265.938 418.5 267.738 415.5C269.738 412.5 270.738 409 270.738 405C270.738 400.8 269.738 397.3 267.738 394.5C265.938 391.5 263.238 389.3 259.638 387.9C256.038 386.3 251.438 385.5 245.838 385.5H205.938ZM417.196 496.5C417.196 499.1 417.796 501.3 418.996 503.1C420.396 504.7 422.096 506 424.096 507C426.296 507.8 428.596 508.2 430.996 508.2C434.796 508.2 438.396 507.4 441.796 505.8C445.396 504 448.296 501.3 450.496 497.7C452.896 493.9 454.096 489 454.096 483L458.896 501C458.896 509.8 456.696 517.4 452.296 523.8C447.896 530 441.896 534.8 434.296 538.2C426.696 541.4 418.096 543 408.496 543C399.496 543 391.196 541.4 383.596 538.2C375.996 535 369.896 530.2 365.296 523.8C360.696 517.2 358.396 509.2 358.396 499.8C358.396 486.6 362.996 476.3 372.196 468.9C381.596 461.3 395.096 457.5 412.696 457.5C419.896 457.5 426.796 458.1 433.396 459.3C440.196 460.5 446.196 462.2 451.396 464.4C456.796 466.4 460.996 469 463.996 472.2V495.9C460.596 492.9 455.996 490.5 450.196 488.7C444.596 486.9 438.796 486 432.796 486C428.996 486 425.896 486.4 423.496 487.2C421.296 488 419.696 489.2 418.696 490.8C417.696 492.2 417.196 494.1 417.196 496.5ZM386.896 450.6L369.196 412.8C378.796 409 389.596 405.5 401.596 402.3C413.796 399.1 426.496 397.5 439.696 397.5C452.696 397.5 464.496 399.3 475.096 402.9C485.696 406.5 494.096 411.8 500.296 418.8C506.496 425.6 509.596 434 509.596 444V540H454.096V458.4C454.096 455.8 453.696 453.4 452.896 451.2C452.096 449 450.796 447.1 448.996 445.5C447.196 443.7 444.796 442.4 441.796 441.6C438.796 440.6 435.196 440.1 430.996 440.1C425.396 440.1 419.496 440.8 413.296 442.2C407.096 443.4 401.596 444.9 396.796 446.7C391.996 448.3 388.696 449.6 386.896 450.6Z"
          fill="var(--bg-page)"
        />
      </svg>
    );
  }

  const fId = `am-fill-${uid}`;
  const f0Id = `am-shadow-${uid}`;
  const f1Id = `am-shadow2-${uid}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 716 716"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        flexShrink: 0,
        ...style,
      }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient
          id={fId}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(-710.262 709.698 -663.207 -481.925 715.34 0)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#59FFCD" />
          <stop offset="1" stopColor="#00BB6D" />
        </radialGradient>
        <filter
          id={f0Id}
          x="44.1318"
          y="0"
          width="671.208"
          height="671.208"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="-4" dy="4" />
          <feGaussianBlur stdDeviation="0.5" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.744891 0 0 0 0 1 0 0 0 0 0.731464 0 0 0 1 0"
          />
          <feBlend mode="normal" in2="shape" result={`effect1_${uid}`} />
        </filter>
        <filter
          id={f1Id}
          x="159.338"
          y="328"
          width="352.259"
          height="219"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="-2" dy="2" />
          <feGaussianBlur stdDeviation="10" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
          />
          <feBlend mode="normal" in2="shape" result={`effect2_${uid}`} />
        </filter>
      </defs>
      <g filter={`url(#${f0Id})`}>
        <path
          d="M673.34 0H87.1318C63.9359 0 45.1318 18.8041 45.1318 42V628.208C45.1318 651.404 63.9359 670.208 87.1319 670.208H673.34C696.536 670.208 715.34 651.404 715.34 628.208V42C715.34 18.804 696.536 0 673.34 0Z"
          fill={`url(#${fId})`}
        />
      </g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.64148 60.3638C8.75718 60.3638 11.283 62.8895 11.283 66.0052V662.057C11.283 685.253 30.087 704.057 53.283 704.057H660.053C663.169 704.057 665.695 706.582 665.695 709.698C665.695 712.814 663.169 715.34 660.053 715.34H42C18.804 715.34 0 696.536 0 673.34V66.0052C0 62.8895 2.52578 60.3638 5.64148 60.3638Z"
        fill="#04A162"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.2074 37.7979C31.3231 37.7979 33.8489 40.3236 33.8489 43.4393V639.491C33.8489 662.687 52.6529 681.491 75.8489 681.491H682.619C685.735 681.491 688.261 684.016 688.261 687.132C688.261 690.248 685.735 692.774 682.619 692.774H64.5659C41.3699 692.774 22.5659 673.97 22.5659 650.774V43.4393C22.5659 40.3236 25.0917 37.7979 28.2074 37.7979Z"
        fill="#05B46C"
      />
      <g filter={`url(#${f1Id})`}>
        <path
          d="M224.838 450H292.338L352.338 540H275.838L224.838 450ZM163.338 330H229.338V540H163.338V330ZM205.938 385.5V330H251.838C272.438 330 289.138 333.1 301.938 339.3C314.938 345.5 324.438 354 330.438 364.8C336.638 375.6 339.738 388 339.738 402C339.738 415.8 336.638 428.2 330.438 439.2C324.438 450 314.938 458.5 301.938 464.7C289.138 470.9 272.438 474 251.838 474H205.938V424.5H245.838C251.438 424.5 256.038 423.8 259.638 422.4C263.238 420.8 265.938 418.5 267.738 415.5C269.738 412.5 270.738 409 270.738 405C270.738 400.8 269.738 397.3 267.738 394.5C265.938 391.5 263.238 389.3 259.638 387.9C256.038 386.3 251.438 385.5 245.838 385.5H205.938ZM417.196 496.5C417.196 499.1 417.796 501.3 418.996 503.1C420.396 504.7 422.096 506 424.096 507C426.296 507.8 428.596 508.2 430.996 508.2C434.796 508.2 438.396 507.4 441.796 505.8C445.396 504 448.296 501.3 450.496 497.7C452.896 493.9 454.096 489 454.096 483L458.896 501C458.896 509.8 456.696 517.4 452.296 523.8C447.896 530 441.896 534.8 434.296 538.2C426.696 541.4 418.096 543 408.496 543C399.496 543 391.196 541.4 383.596 538.2C375.996 535 369.896 530.2 365.296 523.8C360.696 517.2 358.396 509.2 358.396 499.8C358.396 486.6 362.996 476.3 372.196 468.9C381.596 461.3 395.096 457.5 412.696 457.5C419.896 457.5 426.796 458.1 433.396 459.3C440.196 460.5 446.196 462.2 451.396 464.4C456.796 466.4 460.996 469 463.996 472.2V495.9C460.596 492.9 455.996 490.5 450.196 488.7C444.596 486.9 438.796 486 432.796 486C428.996 486 425.896 486.4 423.496 487.2C421.296 488 419.696 489.2 418.696 490.8C417.696 492.2 417.196 494.1 417.196 496.5ZM386.896 450.6L369.196 412.8C378.796 409 389.596 405.5 401.596 402.3C413.796 399.1 426.496 397.5 439.696 397.5C452.696 397.5 464.496 399.3 475.096 402.9C485.696 406.5 494.096 411.8 500.296 418.8C506.496 425.6 509.596 434 509.596 444V540H454.096V458.4C454.096 455.8 453.696 453.4 452.896 451.2C452.096 449 450.796 447.1 448.996 445.5C447.196 443.7 444.796 442.4 441.796 441.6C438.796 440.6 435.196 440.1 430.996 440.1C425.396 440.1 419.496 440.8 413.296 442.2C407.096 443.4 401.596 444.9 396.796 446.7C391.996 448.3 388.696 449.6 386.896 450.6Z"
          fill="#038D57"
        />
      </g>
    </svg>
  );
}
